import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, InjectionToken } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppInsights } from 'applicationinsights-js';
import { combineLatest, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AppState } from 'src/store';
import { BaseEntity } from 'src/store/base/base.model';
import { selectBaseById } from 'src/store/base/base.selectors';
import { selectCompanyById } from 'src/store/company/company.selectors';
import { EmployeeEntity } from 'src/store/employee/employee.model';
import { filterNull } from 'src/util/rxjs/operators';
import { getTrailingDigits } from 'src/util/string';
import { UserViewModel } from 'src/view-models/user-view-model';
import { AuthenticationService } from '../shared/authentication/auth.service';
import { BaseHttpClient } from '../shared/flight-log-api/bases/base-client.service';
import { EmployeeHttpClient } from '../shared/flight-log-api/employees/employee-client.service';
import { FlightLogApiHttpClient } from '../shared/flight-log-api/flight-log-api.service';

export function getModel(
  store: Store<AppState>,
  employeeId: string,
  baseHttp: BaseHttpClient,
  orgHttp: FlightLogApiHttpClient,
) {
  return store
    .select<EmployeeEntity | undefined>((s) => s.employees.entities[employeeId])
    .pipe(
      filterNull(),
      switchMap((emp) => {
        const base$ = emp.baseId != null ? getBase(store, baseHttp, emp.baseId) : of(undefined);
        const company$ = getCompany(store, orgHttp, emp.companyId);
        return combineLatest([base$, company$, of(emp)]);
      }),
      map(([base, company, employee]) => {
        return new UserViewModel(employee, company, base, store);
      }),
    );
}

function getBase(
  store: Store<AppState>,
  baseHttp: BaseHttpClient,
  baseId: string,
): Observable<BaseEntity | undefined> {
  return store.select(selectBaseById(baseId)).pipe(
    switchMap((base) => {
      return base != null ? of(base) : baseHttp.getById(baseId);
    }),
  );
}

function getCompany(store: Store<AppState>, orgHttp: FlightLogApiHttpClient, companyId: string) {
  return store.select(selectCompanyById(companyId)).pipe(
    switchMap((company) => {
      return company != null ? of(company) : orgHttp.getCompany(companyId);
    }),
    filterNull(),
  );
}

@Injectable({ providedIn: 'root' })
export class CurrentUser {
  private readonly _authenticated$ = new ReplaySubject<UserViewModel | undefined>(1);
  private _authenticated?: UserViewModel;
  private readonly _impersonated$ = new ReplaySubject<UserViewModel | undefined>(1);
  private _impersonated?: UserViewModel;
  private readonly _current$ = new ReplaySubject<UserViewModel | undefined>(1);
  private _current?: UserViewModel;

  get valueChanges(): Observable<UserViewModel | undefined> {
    return this._current$.asObservable();
  }

  get value(): UserViewModel | undefined {
    return this._current;
  }

  get authenticated(): {
    value?: UserViewModel;
    valueChanges: Observable<UserViewModel | undefined>;
  } {
    return {
      value: this._authenticated,
      valueChanges: this._authenticated$.asObservable(),
    };
  }

  get impersonated(): {
    value?: UserViewModel;
    valueChanges: Observable<UserViewModel | undefined>;
  } {
    return {
      value: this._impersonated,
      valueChanges: this._impersonated$.asObservable(),
    };
  }

  constructor(
    private _store: Store<AppState>,
    private _auth: AuthenticationService,
    private _employee: EmployeeHttpClient,
    private _base: BaseHttpClient,
    private _api: FlightLogApiHttpClient,
  ) {
    const authenticated$: Observable<
      UserViewModel | undefined
    > = this._auth.claims.valueChanges.pipe(
      distinctUntilChanged(),
      filterNull(),
      switchMap(({ email, EmployeeNumber }) => {
        const employeeId = EmployeeNumber != null ? getTrailingDigits(EmployeeNumber) : undefined;
        if (employeeId != null) {
          return this._employee.getByEmployeeId(employeeId);
        } else if (email != null) {
          return this._employee.getByEmail(email);
        } else {
          return of(undefined);
        }
      }),
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of(undefined);
        }
        return throwError(err);
      }),
      switchMap((employee) => {
        if (employee == null) {
          return of(undefined);
        }
        return getModel(this._store, employee.id, this._base, this._api);
      }),
      distinctUntilChanged(),
    );
    const impersonated$: Observable<
      UserViewModel | undefined
    > = this._auth.impersonatedUserId.valueChanges.pipe(
      distinctUntilChanged(),
      switchMap((userId) => {
        if (userId == null) {
          return of(undefined);
        }
        return this._employee.getById(userId);
      }),
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of(undefined);
        }
        return throwError(err);
      }),
      switchMap((employee) => {
        if (employee == null) {
          return of(undefined);
        }
        return getModel(this._store, employee.id, this._base, this._api);
      }),
      distinctUntilChanged(),
    );

    impersonated$.subscribe((impersonated) => {
      this._impersonated = impersonated;
      this._impersonated$.next(impersonated);
    });

    authenticated$.subscribe((authenticated) => {
      this._authenticated = authenticated;
      this._authenticated$.next(authenticated);
    });
    this.authenticated.valueChanges
      .pipe(
        switchMap((authenticated) => {
          return this.impersonated.valueChanges.pipe(
            map((impersonated) => {
              return impersonated ?? authenticated;
            }),
          );
        }),
        distinctUntilChanged(),
      )
      .subscribe((current) => {
        this._current = current;
        this._current$.next(current);
        if (current != null) {
          AppInsights.setAuthenticatedUserContext(current.email);
        } else {
          AppInsights.clearAuthenticatedUserContext();
        }
      });
  }
}

export const USER = new InjectionToken<UserViewModel>('authenticatied.user');

export function provideUser(currentUser: CurrentUser): UserViewModel {
  if (currentUser.value != null) {
    return currentUser.value;
  }
  throw new Error(
    'Could not provide the current user. Ensure it is only being injected into components that are initialized',
  );
}
